// src/api/invoicingApi.ts
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { isSessionOver } from './axiosInstance';

/**
 * Dedicated axios instance for the owner-only invoicing screen.
 *
 * It deliberately does NOT reuse the shared `api` instance: that one dispatches `logout()` on any
 * 403 (see api/axiosInstance.ts), and the invoicing endpoints answer 403 for reasons that are not
 * "your session expired" — e.g. an account that is `isAdmin` in the UI but lacks the backend
 * `c_role = 'ADMIN'`, or a denied continuation of an already-authorized request. Reusing it would
 * sign the owner out of the whole site mid-sync/export. Same auth header, its own errors on screen.
 *
 * None of that applies to a dead session, and a dead session is no longer distinguishable by status
 * alone: the API answers a missing, expired or invalid token with 403 too. `isSessionOver` reads
 * the error code the auth entry point puts in the body, so those clear the store while an
 * authorisation refusal still reaches the screen. It matters here more than anywhere: the export
 * and PDF downloads use `responseType: 'blob'`, so their error body is not a parsed object, and a
 * status-only check would have left the operator stuck on a screen whose every button fails.
 */
const invoicingApi = axios.create({
    baseURL: config.API_URL,
});

invoicingApi.interceptors.request.use(
    (requestConfig) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => Promise.reject(error)
);

invoicingApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Session over, never a plain authorisation 403. That distinction is the whole reason this
        // instance exists, and isSessionOver draws it from the body rather than the status so it
        // keeps holding when the server goes back to answering 401.
        if (await isSessionOver(error)) {
            store.dispatch(logout());
        }
        return Promise.reject(error);
    }
);

export default invoicingApi;
